/**
 * SurfacePersona - Custom persona for surface workflow
 *
 * Extends BasePersona to add surface selection state tracking.
 * Manages conditional tool availability based on surface selection.
 * When a surface is selected, enables tools that extend SurfaceToolBase
 * and can switch to a view aligned with the surface plane.
 */

import { BasePersona } from '../../BasePersona';
import { PointoramaSurface } from '../../../utils/PointoramaSurface';
import { SurfaceToolBase } from '../../tools/implementations/SurfaceToolBase';

export interface SurfaceData {
  groupIdentifier: string;
  name: string;
  plane: {
    position: { x: number; y: number; z: number };
    normal: { x: number; y: number; z: number };
  };
  tolerance?: number;
}

interface SurfaceSelectionEvent {
  selectedSurfaceIdentifier: string | null;
  surface: { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } } | null;
}

interface SurfaceObjectsDeletedEvent {
  deletedGroupIdentifiers: string[];
}

interface SurfaceObjectsChangedEvent {
  groups: { type: string }[];
  surfacesData: SurfaceData[];
}

export class SurfacePersona extends BasePersona {
  private hasActiveSurface = false;
  private surfacesExist = false;

  // Surface data storage - single source of truth
  private surfacesData: SurfaceData[] = [];
  private selectedSurfaceIdentifier: string | null = null;
  private selectedSurface: PointoramaSurface | null = null;

  // Track whether the camera has been aligned to the current surface
  // This is shared across all SurfaceToolBase tools to prevent jumping when switching tools
  private _hasAlignedToCurrentSurface = false;

  // Saved 2D camera state for the current surface
  // Used to restore camera position when switching back from 3D to 2D
  private _saved2DCameraState: {
    x: number;
    y: number;
    z: number;
    radius: number;
  } | null = null;

  /**
   * Override enable to set up surface selection listeners
   */
  protected onEnable(): void {
    super.onEnable();

    this.viewer.floorIsolationManager.enterCustomMode();

    // Listen for objects changed, selection changed, and deletion events
    this.viewer.scene.addEventListener('objects_changed', this.handleObjectsChanged);
    this.viewer.scene.addEventListener('objects_selection_changed', this.handleSelectionChanged);
    this.viewer.scene.addEventListener('objects_deleted', this.handleObjectsDeleted);

    // Emit initial state (tools disabled by default until surface is selected)
    this.updateToolAvailability();

    // Request current objects list from client after listeners are set up
    // Use longer timeout to ensure the response is processed before updating tool availability
    setTimeout(() => {
      this.viewer.scene.dispatchEvent({
        type: 'objects_request',
        value: {},
      });

      // After objects_request is processed, update tool availability again
      // This ensures tools are highlighted if a surface was already selected
      setTimeout(() => {
        this.updateToolAvailability();
      }, 50);
    }, 0);
  }

  /**
   * Override disable to clean up listeners
   */
  protected onDisable(): void {
    this.viewer.scene.removeEventListener('objects_changed', this.handleObjectsChanged);
    this.viewer.scene.removeEventListener('objects_selection_changed', this.handleSelectionChanged);
    this.viewer.scene.removeEventListener('objects_deleted', this.handleObjectsDeleted);

    this.viewer.floorIsolationManager.exitCustomMode();

    // Reset state
    this.hasActiveSurface = false;
    this.surfacesExist = false;

    super.onDisable();
  }

  /**
   * Override onModeChanged to request current selection when switching modes
   * This ensures tools are enabled if a surface is already selected
   */
  protected onModeChanged(): void {
    // Update tool availability after mode switch is complete
    this.updateToolAvailability();
  }

  /**
   * Handle selection changed event
   */
  private handleSelectionChanged = (event: SurfaceSelectionEvent): void => {
    const selectedSurfaceIdentifier = event.selectedSurfaceIdentifier;
    const surfaceData = event.surface as { position: { x: number; y: number; z: number }; normal: { x: number; y: number; z: number } } | null;

    // Only process when a new surface is selected (not on deselect or same surface)
    if (!selectedSurfaceIdentifier || !surfaceData) {
      return;
    }

    // Store previous surface identifier to detect surface changes
    const previousSurfaceIdentifier = this.selectedSurfaceIdentifier;
    this.selectedSurfaceIdentifier = selectedSurfaceIdentifier;

    // Reset alignment flag and saved camera state when surface changes (so camera jumps to the new surface)
    if (previousSurfaceIdentifier !== this.selectedSurfaceIdentifier) {
      this._hasAlignedToCurrentSurface = false;
      this._saved2DCameraState = null;
    }

    // Create or update PointoramaSurface from surface data
    // Create a new PointoramaSurface or reuse existing
    if (!this.selectedSurface || this.selectedSurface.identifier !== selectedSurfaceIdentifier) {
      this.selectedSurface = new PointoramaSurface({
        identifier: selectedSurfaceIdentifier,
      });
      // Note: Plane and tolerance are loaded from reactive properties in constructor
    }
    // If surface already exists, it's already connected to reactive properties - no update needed

    // Check if a surface is selected
    const hadActiveSurface = this.hasActiveSurface;
    this.hasActiveSurface = true;

    // Only update tool availability when a NEW surface is selected
    if (!hadActiveSurface) {
      // Surface was selected - update tool availability
      this.updateToolAvailability();
    }

    // Notify tools about surface selection change
    // Note: View alignment is handled by SurfaceToolBase when a surface tool is active
    this.dispatchEvent({
      type: 'surface_selection_changed',
      selectedSurfaceIdentifier: this.selectedSurfaceIdentifier,
      surface: this.selectedSurface,
    });

    // Update surface visibility isolation
    this.viewer.scene.dispatchEvent({
      type: 'surface_isolate_visibility',
      value: {
        groupIdentifier: selectedSurfaceIdentifier,
        isolate: true,
      },
    });
  };

  /**
   * Handle objects deleted event
   */
  private handleObjectsDeleted = (event: SurfaceObjectsDeletedEvent): void => {
    const deletedGroupIdentifiers = event.deletedGroupIdentifiers || [];

    // Check if the currently selected surface was deleted
    if (this.selectedSurfaceIdentifier && deletedGroupIdentifiers.includes(this.selectedSurfaceIdentifier)) {
      // Clear selection state
      this.selectedSurfaceIdentifier = null;
      this.selectedSurface = null;
      this.hasActiveSurface = false;
      this._hasAlignedToCurrentSurface = false;
      this._saved2DCameraState = null;

      // Update tool availability (will switch to surface_selection tool)
      this.updateToolAvailability();

      // Restore visibility of all surfaces
      this.viewer.scene.dispatchEvent({
        type: 'surface_isolate_visibility',
        value: {
          groupIdentifier: null,
          isolate: false,
        },
      });
    }
  };

  /**
   * Handle objects changed event to check for surface existence and store surface data
   */
  private handleObjectsChanged = (event: SurfaceObjectsChangedEvent): void => {
    const groups = event.groups || [];
    const surfacesData = event.surfacesData || [];

    // Store surfaces data
    this.surfacesData = surfacesData;

    // Check if any surface groups exist (type 'SURFACE' or however they're identified)
    const surfaceGroupsExist = groups.some((group) => group.type === 'SURFACE');

    if (this.surfacesExist !== surfaceGroupsExist) {
      this.surfacesExist = surfaceGroupsExist;
      this.updateToolAvailability();
    }

    // Notify tools about surface data change
    this.dispatchEvent({
      type: 'surface_data_changed',
      surfacesData: this.surfacesData,
    });
  };

  /**
   * Update tool availability based on surface selection state
   * Disables tools that extend SurfaceToolBase when no surface is selected
   */
  private updateToolAvailability(): void {
    // Get all tools that require a surface (extend SurfaceToolBase)
    const surfaceTools = Array.from(this.tools.entries()).filter(([_, tool]) => tool instanceof SurfaceToolBase);

    // If no surface is selected and one of these tools is active, switch back to the
    // default selection tool.
    if (!this.hasActiveSurface) {
      const activeSurfaceTool = surfaceTools.find(([_, tool]) => tool.enabled);
      if (activeSurfaceTool) {
        const selectionTool = this.tools.get('selection');
        if (selectionTool) {
          this.enableTool('selection');
        }
      }
    }

    // Dispatch event to notify UI about tool availability changes
    // hasActiveSurface is true only when surfaces exist AND one is selected
    this.dispatchEvent({
      type: 'tool_availability_changed',
      toolsRequiringSurface: surfaceTools.map(([name]) => name),
      hasActiveSurface: this.hasActiveSurface,
      surfacesExist: this.surfacesExist,
    });
  }

  /**
   * Override enableTool to prevent enabling tools that require a surface
   * and to reset camera alignment when switching from a non-surface tool to a surface tool
   */
  enableTool(toolName: string): void {
    // Check if tool requires a surface (extends SurfaceToolBase) and none is selected
    const tool = this.tools.get(toolName);
    if (!this.hasActiveSurface && tool instanceof SurfaceToolBase) {
      // Silently ignore the request (tool remains disabled)
      return;
    }

    // Check if switching from a non-surface tool to a surface tool
    // If so, reset alignment flag so camera will center on the surface
    if (tool instanceof SurfaceToolBase) {
      const currentlyEnabledTools = this.getEnabledTools();
      const wasNonSurfaceToolActive = currentlyEnabledTools.every((t) => !(t instanceof SurfaceToolBase));
      if (wasNonSurfaceToolActive) {
        // Reset alignment flag so the new surface tool will align camera to surface
        this._hasAlignedToCurrentSurface = false;
        this._saved2DCameraState = null;
      }
    }

    // Otherwise, proceed with normal enableTool logic
    super.enableTool(toolName);
  }

  /**
   * Check if a tool is available (not disabled due to missing requirements)
   */
  isToolAvailable(toolName: string): boolean {
    const tool = this.tools.get(toolName);
    if (tool instanceof SurfaceToolBase) {
      return this.hasActiveSurface;
    }
    return true;
  }

  // ==================== Surface Data Accessors ====================

  /**
   * Get all surfaces data
   */
  getSurfacesData(): SurfaceData[] {
    return this.surfacesData;
  }

  /**
   * Get the currently selected surface identifier
   */
  getSelectedSurfaceIdentifier(): string | null {
    return this.selectedSurfaceIdentifier;
  }

  /**
   * Get the currently selected surface
   */
  getSelectedSurface(): PointoramaSurface | null {
    return this.selectedSurface;
  }

  /**
   * Check if a surface is currently selected
   */
  hasSurfaceSelected(): boolean {
    return this.hasActiveSurface;
  }

  /**
   * Check if any surfaces exist in the project
   */
  doSurfacesExist(): boolean {
    return this.surfacesExist;
  }

  /**
   * Check if the camera has been aligned to the current surface
   * Used by SurfaceToolBase to prevent jumping when switching between surface tools
   */
  hasAlignedToCurrentSurface(): boolean {
    return this._hasAlignedToCurrentSurface;
  }

  /**
   * Mark that the camera has been aligned to the current surface
   * Called by SurfaceToolBase after aligning the camera
   */
  setAlignedToCurrentSurface(aligned: boolean): void {
    this._hasAlignedToCurrentSurface = aligned;
  }

  /**
   * Get the saved 2D camera state for the current surface
   * Used to restore camera position when switching back from 3D to 2D
   */
  getSaved2DCameraState(): { x: number; y: number; z: number; radius: number } | null {
    return this._saved2DCameraState;
  }

  /**
   * Save the current 2D camera state for the current surface
   * Called by SurfaceToolBase before switching to 3D view
   */
  save2DCameraState(state: { x: number; y: number; z: number; radius: number }): void {
    this._saved2DCameraState = state;
  }

  /**
   * Clear the saved 2D camera state
   */
  clearSaved2DCameraState(): void {
    this._saved2DCameraState = null;
  }
}
